
import React, { useState, useEffect, useMemo } from "react";
import { Card, Layout, Col, Button, Form, Row, Comment, Divider, Input } from "antd";

import useRootStore from "../../store/useRootStore";

import { PdfChapterEndnotes } from "../../components/Previewer/print/types";
import EditTOC from "../../components/Editor/EditTOC";
import { getTitleChapterData } from "../../utils/volumes";
import { parseFormattableEndnote } from "../../press/exportEpub/parsers/formattableEndNote";
import { GetThemeFromIDB } from "../../utils/offline.book.helpers";
import { Button as AtticusButton } from "../../components/Shared/Buttons/Button";
import { FullPageImage } from "./fullpageimage";
import { useHistory } from "react-router-dom";
import { ScrollContainer } from "../../components/Shared/Layouts";
import Can from "../../components/casl/Can";
import { debounce } from "lodash";

const EditButton = ({ bookId }: { bookId: string }) => {
  const { push } = useHistory();
  return (
    <Button className="btn-a" type="primary" onClick={() => push(`/books/${bookId}`)}>Edit book details</Button>
  );
};

const ConvertButton = () => {
  const { chapterMeta, syncActiveChapterMeta } = useRootStore().chapterStore;
  const { refreshCache } = useRootStore().pdfCacheStore;
  const { setIsChapterLocalUpdate, setChangeEvent } = useRootStore().bookSyncWebSocketStore;

  const convertToFullPageImage = () => {
    setIsChapterLocalUpdate(true);
    setChangeEvent("full-page-image-chapter-change");
    syncActiveChapterMeta({
      type: "image",
      configuration: { showInTableOfContents: false },
    });
    // invalidate only the current chapter
    refreshCache(chapterMeta.bookId, "full-page-image-chapter-change", {
      "full-page-image-chapter-change": { chapterId: chapterMeta._id },
    });
  };

  return (
    <AtticusButton
      type="at-secondary"
      size="small"
      color="blue"
      onClick={() => convertToFullPageImage()}
    >
      {"Convert to full page image"}
    </AtticusButton>
  );
};

export const MetaContainer = () => {
  const { book, getAllEndNotesByChapter } = useRootStore().bookStore;
  const { setSidebarPreviewerOnlyMode, setSettingView, editor_setting } = useRootStore().appStore;
  const { refreshCache } = useRootStore().pdfCacheStore;
  const { setIsBookLocalUpdate, setChangeEvent, setSyncChapterId} = useRootStore().bookSyncWebSocketStore;
  const [endNotes, setEndNotes] = useState<(PdfChapterEndnotes)[]>([]);
  const [theme, setTheme] = useState<(IThemeStore.ThemeStyleProps)>();
  const [chapterMetadataForm] = Form.useForm();
  const { chapterMeta, syncActiveChapterMeta } = useRootStore().chapterStore;

  const { titleType, publisherName, title, subtitle, author, publisherLink } = useMemo(() => getTitleChapterData(book, chapterMeta), [chapterMeta, book]);

  const debouncedSyncChapterActive = debounce(syncActiveChapterMeta, 1000);
  
  useEffect(() => {
    /** 
     * 1) Set the RHS Sidebar to the Previewer only mode for isWithoutEditor() chapters.
     * 2) Hide the RHS Sidebar unless the Previewer is selected when switching the chapter to a isWithoutEditor() chapter.
     * */
    setSidebarPreviewerOnlyMode(true);
    setSettingView({
      show: editor_setting.view === "previewer" ? editor_setting.show : false,
      view: "previewer",
    });

    getAllEndNotesByChapter().then(notes => {
      setEndNotes(notes);
    });
    const getTheme = async () => {
      const theme = await GetThemeFromIDB(book.themeId);
      setTheme(theme?.properties);
    };
    getTheme();
  }, []);

  useEffect(() => {
    // this is to update form values when the chapter changes,
    // since lib won't update the them automatically
    chapterMetadataForm.resetFields();
  }, [chapterMeta]);

  function handleChange(_, values) {
    setIsBookLocalUpdate(true);
    setChangeEvent("chapter-title-change");
    setSyncChapterId(chapterMeta._id);
    debouncedSyncChapterActive({
      title: values.title,
      subtitle: values.subtitle,
      ...(values.author !== undefined && chapterMeta.volume
        ? {
          volume: {
            ...chapterMeta.volume,
            author: values.author,
          },
        }
        : {}),
    });
    refreshCache(chapterMeta.bookId, "chapter-title-change", { "chapter-title-change": { chapterId: chapterMeta._id } });
  }
  const CommentBlock = ({ label, value }) => (
    <Comment
      author={<span className="title-page-comment-label">{label}</span>}
      content={<h4 className="title-page-content-label">{value || "-"}</h4>}
    />
  );

  switch (chapterMeta.type) {
    case "title":
      return (
        <Layout.Content>
          <Card className="title-page-card">
            <Row className="title-page-card-content">
              <div className="container-profile">
                <Col span={12}>
                  <h4>
                    Title page is auto-generated based on contents of the{" "}
                    {titleType}
                  </h4>
                </Col>
                <Col span={12} className="title-page-card-button-container">
                  <Can action={"view"} subject={"convert-to-full-page-image"}>
                      <ConvertButton />
                  </Can>
                  <Can action={"view"} subject={"edit-book-details"}>
                    {titleType === "book" && <EditButton bookId={book._id} />}
                  </Can>
                </Col>
              </div>
            </Row>
          </Card>
          <div className="title-page-area">
            <ScrollContainer>
              <div className="att-stats potted">
                <h2>Title Page</h2>
                <CommentBlock
                  label={(titleType === "book" ? "" : titleType + " ") + "Title"}
                  value={title}
                />
                <CommentBlock
                  label={
                    (titleType === "book" ? "" : titleType + " ") + "Subtitle"
                  }
                  value={subtitle}
                />
                {(titleType === "book" || titleType === "volume") && (
                  <CommentBlock
                    label={"Author"}
                    value={author.join(", ") || "-"}
                  />
                )}
                {titleType === "book" && (
                  <>
                    <CommentBlock label={"Publisher name"} value={publisherName} />
                    <CommentBlock
                      label={"Publisher link"}
                      value={publisherLink}
                    />
                  </>
                )}
              </div>
            </ScrollContainer>
          </div>
        </Layout.Content>
      );
    case "toc":
      return <EditTOC />;
    case "image":
      return <FullPageImage />;
    case "endnotes":
      return (
        <Layout.Content>
          <Card>
            <Form
              form={chapterMetadataForm}
              onValuesChange={handleChange}
              initialValues={{ title: chapterMeta.title }}
            >
              <Form.Item name="title" label="Custom endnotes title">
                <Input />
              </Form.Item>
            </Form>
            <div className="att-toc">
              <h3>
                Endnotes are auto-generated based on the endnotes used in the
                book
              </h3>
            </div>
          </Card>

          <div className="att-toc-list potted">
            {endNotes.map((endNote, i) => (
              <div key={i}>
                <div >{endNote.chapterTitle} </div>
                {endNote.chapterEndnotes.map((chapterEndnotes, j) => (

                  <div className="item" key={j}>
                    <span className="name">
                      {chapterEndnotes.type === "endnote" && (
                        <div className="endnote-wrapper">
                          {`${j + 1}. `}
                          <div className="endnote"
                            dangerouslySetInnerHTML={{
                              __html: parseFormattableEndnote(chapterEndnotes.note, chapterMeta, theme),
                            }}
                          ></div>
                        </div>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Layout.Content>
      );
    case "part":
      return (
        <Layout.Content>
          <Card>
            <div className="att-toc">
              <h3>A Part will <b>NOT</b> introduce a page to the book. However it has a "Title" chapter</h3>
            </div>
          </Card>
          <Divider />
          <div className="potted">
            <Form
              form={chapterMetadataForm}
              onValuesChange={handleChange}
              labelCol={{ xs: 24, sm: 6, md: 6, lg: 7, xl: 7 }}
              wrapperCol={{ xs: 24, sm: 18, md: 16, lg: 12, xl: 12 }}
              initialValues={{
                title: chapterMeta.title,
                subtitle: chapterMeta.subtitle,
              }}
            >
              <Form.Item name="title" label="Part title">
                <Input />
              </Form.Item>
              <Form.Item name="subtitle" label="Part subtitle">
                <Input />
              </Form.Item>
            </Form>
          </div>
        </Layout.Content>
      );
    case "volume":
      return (
        <Layout.Content>
          <Card>
            <div className="att-toc">
              <h3>A Volume will <b>NOT</b> introduce a page to the book. However it has "Title" & "Table of Contents" chapters</h3>
            </div>
          </Card>
          <Divider />
          <div className="potted">
            <Form
              form={chapterMetadataForm}
              onValuesChange={handleChange}
              labelCol={{ xs: 24, sm: 6, md: 6, lg: 7, xl: 7 }}
              wrapperCol={{ xs: 24, sm: 18, md: 16, lg: 12, xl: 12 }}
              initialValues={{
                title: chapterMeta.title,
                subtitle: chapterMeta.subtitle,
                author: chapterMeta.volume?.author,
              }}
            >
              <Form.Item name="title" label="Volume title">
                <Input />
              </Form.Item>
              <Form.Item name="subtitle" label="Volume subtitle">
                <Input />
              </Form.Item>
              <Form.Item name="author" label="Volume author">
                <Input />
              </Form.Item>
            </Form>
          </div>
        </Layout.Content>
      );
  }
  return null;
};
